// Single-slot alarm/preview player shared by every reminder picker's preview
// button (tasks/events, habits — both the built-in sound list and a custom
// device-picked sound), plus the real Tier-1 alarm playback, so starting a
// new one (or stopping) always stops whatever was playing before — no
// overlapping/orphaned sounds. Clicking the same option's preview button
// again pauses it instead of restarting, and a `listeners` pub-sub lets any
// mounted preview button re-render to show play vs. pause without each
// caller having to manage its own audio state.
import { registerPlugin, Capacitor } from "@capacitor/core";

// Native-only: launches Android's own sound picker (same one the Clock app
// uses) and plays back a picked content:// URI — see SoundPickerPlugin.java.
// Registering this is a no-op on web/PWA; calls below are always gated
// behind Capacitor.isNativePlatform() so it's never actually invoked there.
const SoundPicker = registerPlugin("SoundPicker");

// A custom sound's "id" for playing/subscription purposes is its URI,
// prefixed so it can never collide with a built-in ALARM_SOUND_OPTIONS id.
export const customSoundId = (uri) => `custom:${uri}`;

let active = null; // { id, kind: "builtin" | "custom", audio? } | null
const listeners = new Set();

function notify() {
  const id = active?.id ?? null;
  listeners.forEach(fn => fn(id));
}

export function playAlarmSoundOption(option) {
  if (active?.id === option?.id) { stopAlarmSound(); return; } // second click on the same row = pause
  stopAlarmSound();
  if (!option?.file) return;
  const audio = new Audio(option.file);
  audio.addEventListener("ended", () => { if (active?.audio === audio) stopAlarmSound(); });
  audio.play().catch(() => {});
  active = { id: option.id, kind: "builtin", audio };
  notify();
}

// Plays a custom device-picked sound (native only) by its content:// URI.
export function playCustomAlarmSound(uri) {
  const id = customSoundId(uri);
  if (active?.id === id) { stopAlarmSound(); return; }
  stopAlarmSound();
  if (!uri || !Capacitor.isNativePlatform()) return;
  active = { id, kind: "custom" };
  notify();
  SoundPicker.previewSound({ uri }).catch(() => {});
}

export function stopAlarmSound() {
  if (!active) return;
  if (active.kind === "builtin") { try { active.audio.pause(); active.audio.currentTime = 0; } catch {} }
  else { SoundPicker.stopPreview().catch(() => {}); }
  active = null;
  notify();
}

export function getPlayingAlarmSoundId() {
  return active?.id ?? null;
}

// Returns an unsubscribe function. `fn` is called with the currently-playing
// option id (or null) whenever playback starts/stops.
export function subscribeAlarmSound(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Opens Android's system sound picker and resolves { uri, label }, or null
// if the user cancelled. Native only — callers should gate the UI that
// invokes this behind Capacitor.isNativePlatform() (same as the built-in
// Alarm/Notification toggle already does).
export async function pickCustomAlarmSound(currentUri) {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const res = await SoundPicker.pickSound({ currentUri: currentUri || "" });
    if (!res || res.cancelled || !res.uri) return null;
    return { uri: res.uri, label: res.label || "Custom sound" };
  } catch {
    return null;
  }
}
